<script setup>
/**
 * Section header that labels a group of related fields within a form.
 *
 * Owns the type role (M3 title-small, the subsection role), its color, and its asymmetric
 * spacing — 24dp above, 8dp below — so the header reads as belonging to the group beneath it.
 * Callers pick none of the three; only grid placement stays with the caller. A container may
 * recolor it by setting `--hr-on-container`, matching HrListSectionHeader inside a colored list.
 *
 * The spacing assumes the 16px rhythm of `q-col-gutter-md`, the standard form gutter.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Labeling a group of related fields inside a form or form dialog
 * @avoidWhen Labeling a group of list items — use HrListSectionHeader
 * @related HrListSectionHeader
 * @a11y Renders a plain element, not a heading — HrFormDialog titles are not headings either,
 *   so there is no level for a subsection to sit under yet.
 */
defineOptions({ name: 'HrFormSectionHeader' });

defineProps({
  /**
   * Header text. Falls back to the default slot when null.
   * @replacesSlot default
   * @example Service delivery
   */
  label: {
    type: String,
    default: null,
  },
});
</script>

<template>
  <div class="hr-form-section-header">
    {{ label }}
    <!-- @slot Header text, as an alternative to the `label` prop. -->
    <slot v-if="label === null" />
  </div>
</template>

<style lang="scss" scoped>
/*
 * A section header belongs to the group below it, so the space above it is larger than the
 * space below: 24dp above, 8dp below, rather than sitting equidistant between two groups.
 *
 * Both are deltas on the 16px vertical rhythm of `q-col-gutter-md`, the standard form
 * gutter, which gives every grid child `padding-top: 16px`. +8 above lands on 24; -8 below
 * takes the *next* row's 16px down to 8. The deltas cancel, so total height is unchanged
 * (16+20+16 and 24+20+8 are both 52) — no reflow and no change to the scroll threshold.
 */
.hr-form-section-header {
  margin-top: 8px;
  margin-bottom: -8px;
  color: var(--hr-on-container, var(--app-on-surface-variant));
  font-size: var(--app-type-title-small-size);
  line-height: var(--app-type-title-small-line-height);
  font-weight: var(--app-type-title-small-weight);
  letter-spacing: var(--app-type-title-small-tracking);
}
</style>
